import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ShoppingBag, Heart, Truck, RotateCcw, Shield, HelpCircle } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import sizeGuideImg from '../../assets/sizeGuide.jpeg';

const SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const MATERIALS = [
  {
    label: 'Performance Stretch',
    priceKey: 'performanceStretchPrice',
    composition: '94% Polyester, 6% Spandex',
    description: 'Flexible and durable for active hospital shifts and frequent movement.',
  },
  {
    label: 'Premium Soft Stretch',
    priceKey: 'premiumStretchPrice',
    composition: '72% Polyester, 22% Rayon, 6% Spandex',
    description: 'Soft, stretchy, and polished—ideal for clinics and patient-facing roles.',
  },
  {
    label: 'Classic Comfort Blend',
    priceKey: 'comfortBlendPrice',
    composition: '65% Polyester, 35% Cotton',
    description: 'A comfortable, traditional scrub fabric for clinics and everyday wear.',
  },
];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


const ProductViewPage = () => {
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [colorVariants, setColorVariants] = useState([]); // sibling products (same item, different color), includes current
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // When we swap colors locally (we already have the sibling's data cached),
  // we update the URL via navigate() for shareability, but that changes
  // `productId` and would otherwise re-trigger a full network refetch below.
  // This ref lets the effect know to skip that refetch just once.
  const skipNextFetchRef = useRef(false);

  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(MATERIALS[0].label);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    // A local color swap already has the sibling's full data cached in
    // colorVariants and has updated `product`/`selectedColor` itself — skip
    // the redundant network round trip this productId change would otherwise cause.
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    const fetchProduct = async () => {
      try {
        setLoading(true);

        const gender = searchParams.get('gender');
        const category = searchParams.get('category');
        const type = searchParams.get('type');

        if (!gender) {
          throw new Error('Missing gender parameter');
        }

        if (!category) {
          throw new Error('Missing category parameter');
        }

        // Build query: category + type (if present) + id
        const params = new URLSearchParams({ gender, category });
        if (type) params.append('type', type);
        params.append('id', productId);

        const response = await fetch(`${BACKEND_URL}/api/products?${params.toString()}`);

        if (response.status === 404) {
          throw new Error('Product not found');
        }
        if (!response.ok) {
          throw new Error('Failed to fetch product');
        }

        const data = await response.json();

        // Handle both response formats
        const fetchedProduct = data.products?.[0] || data.data?.[0];
        if (!fetchedProduct) {
          throw new Error('Product not found');
        }

        setProduct(fetchedProduct);
        // Pre-select the product's own color if available
        setSelectedColor(fetchedProduct.color || '');
        // Material selection defaults to Performance Stretch for every product
        setSelectedMaterial(MATERIALS[0].label);
        setError(null);

        // Fetch sibling color variants (same product, different color) so the
        // color swatches can swap the displayed image/details instantly.
        if (fetchedProduct.groupKey) {
          try {
            const familyParams = new URLSearchParams({ gender, category });
            if (type) familyParams.append('type', type);
            familyParams.append('groupKey', fetchedProduct.groupKey);

            const familyResponse = await fetch(`${BACKEND_URL}/api/products?${familyParams.toString()}`);
            if (familyResponse.ok) {
              const familyData = await familyResponse.json();
              setColorVariants(familyData.products ?? [fetchedProduct]);
            } else {
              // Fall back to just the current product if the family lookup fails
              setColorVariants([fetchedProduct]);
            }
          } catch (familyErr) {
            console.error('Error fetching color variants:', familyErr);
            setColorVariants([fetchedProduct]);
          }
        } else {
          setColorVariants([fetchedProduct]);
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, searchParams]);

  // Quick lookup: color -> full product record for that color variant
  const variantByColor = useMemo(() => {
    const map = {};
    colorVariants.forEach(variant => {
      map[variant.color] = variant;
    });
    return map;
  }, [colorVariants]);

  // The displayed price follows whichever material is selected. Falls back
  // to the product's base price if that material's price field isn't set.
  const selectedMaterialPriceKey = MATERIALS.find(m => m.label === selectedMaterial)?.priceKey;
  const displayedPrice = product?.[selectedMaterialPriceKey] ?? product?.price;

  const handleMaterialSelect = (material) => {
    setSelectedMaterial(material);
  };

  const handleColorSelect = (color) => {
    if (color === selectedColor) return;

    const variant = variantByColor[color];
    if (!variant) {
      // We don't have full data for this color (e.g. family fetch failed) —
      // just update the label rather than leaving the click dead.
      setSelectedColor(color);
      return;
    }

    // Swap the displayed product (image, id, etc.) to the selected color's
    // variant, and keep the URL in sync so this exact color is shareable/
    // deep-linkable, without triggering a redundant refetch.
    skipNextFetchRef.current = true;
    setProduct(variant);
    setSelectedColor(color);
    setSelectedMaterial(MATERIALS[0].label);

    const newPath = location.pathname.replace(productId, variant.id);
    navigate(`${newPath}?${searchParams.toString()}`, { replace: true });
  };

  const handleAddToCart = () => {
    if (!selectedSize) {
      alert('Please select a size');
      return;
    }

    addToCart({
      ...product,
      price: displayedPrice,
      selectedSize,
      selectedColor: selectedColor || 'Default',
      selectedMaterial,
      quantity
    });

    setAddedToCart(true);
    
    // Reset form
    setSelectedSize('');
    setSelectedColor('');
    setSelectedMaterial(MATERIALS[0].label);
    setQuantity(1);

    // Show success message
    setTimeout(() => {
      setAddedToCart(false);
    }, 2000);
  };

  const handleSizeGuideClick = () => {
    window.open(sizeGuideImg, '_blank');
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="bg-navy text-gray-800 min-h-screen flex items-center justify-center">
        <p className="text-gray-500 animate-pulse">Loading product...</p>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────────
  if (error || !product) {
    return (
      <div className="bg-navy text-gray-800 min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-red-600 text-lg">{error || 'Product not found'}</p>
        <button
          onClick={() => navigate(-1)}
          className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition"
        >
          Go Back
        </button>
      </div>
    );
  }

  // ── Page ─────────────────────────────────────────────────────────────────
  return (
    <div className="bg-navy text-gray-800 font-sans min-h-screen">

      {/* Breadcrumb */}
      <div className="bg-navy-light border-b border-primary/20">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center text-sm text-gray-500 gap-2">
            <button onClick={() => navigate('/')} className="hover:text-primary transition">Home</button>
            <span>/</span>
            <button
              onClick={() => navigate(`/${product.category}`)}
              className="hover:text-primary transition capitalize"
            >
              {product.category}
            </button>
            <span>/</span>
            <span className="text-gray-700 truncate max-w-xs">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">

          {/* ── Image ── */}
          <div className="space-y-4">
            <div className="aspect-square bg-navy-light rounded-xl overflow-hidden">
              <img
                src={product.image || product.imageUrl}
                alt={product.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/600x600?text=Image+Not+Found';
                }}
              />
            </div>
          </div>

          {/* ── Details ── */}
          <div className="flex flex-col">

            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="text-sm text-primary font-medium capitalize mb-1">
                  {product.category} · {product.type?.replace(/_/g, ' ')}
                </p>
                <h1 className="text-3xl font-bold text-gray-900">{product.name}</h1>
              </div>
              <button
                onClick={() => setIsWishlisted(!isWishlisted)}
                className="text-gray-500 hover:text-red-600 transition mt-1"
              >
                <Heart size={24} fill={isWishlisted ? 'currentColor' : 'none'} />
              </button>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3 mt-4 mb-6">
              <span className="text-3xl font-bold text-primary">${displayedPrice?.toFixed(2)}</span>
              {product.originalPrice && (
                <>
                  <span className="text-lg text-gray-500 line-through">${product.originalPrice.toFixed(2)}</span>
                  <span className="text-sm font-medium text-red-600">
                    {Math.round(((product.originalPrice - displayedPrice) / product.originalPrice) * 100)}% off
                  </span>
                </>
              )}
            </div>

            {/* Color */}
            {product.availableColors?.length > 0 && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Color: <span className="text-primary capitalize">{selectedColor || 'Select'}</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {product.availableColors.map(color => (
                    <button
                      key={color}
                      onClick={() => handleColorSelect(color)}
                      disabled={!variantByColor[color]}
                      title={!variantByColor[color] ? 'Loading color options…' : undefined}
                      className={`px-3 py-1.5 rounded-md text-sm border-2 capitalize transition disabled:opacity-50 disabled:cursor-not-allowed ${
                        selectedColor === color
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-primary/20 text-gray-600 hover:border-primary/50'
                      }`}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Material */}
            <div className="mb-6">
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-800 mb-2">
                Material: <span className="text-primary">{selectedMaterial}</span>
                <span className="relative group inline-flex">
                  <HelpCircle size={14} className="text-gray-400 hover:text-primary cursor-help" />
                  <div className="hidden group-hover:block absolute z-20 left-1/2 -translate-x-1/2 top-full mt-2 w-72 bg-white border border-primary/20 rounded-lg shadow-lg p-3 text-xs text-gray-600 space-y-3">
                    {MATERIALS.map(material => (
                      <div key={material.label}>
                        <p className="font-semibold text-gray-900">{material.label}</p>
                        <p className="text-gray-500">{material.composition}</p>
                        <p>{material.description}</p>
                      </div>
                    ))}
                  </div>
                </span>
              </label>
              <div className="flex flex-wrap gap-2">
                {MATERIALS.map(material => (
                  <div key={material.label} className="relative group">
                    <button
                      onClick={() => handleMaterialSelect(material.label)}
                      className={`px-3 py-1.5 rounded-md text-sm border-2 transition ${
                        selectedMaterial === material.label
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-primary/20 text-gray-600 hover:border-primary/50'
                      }`}
                    >
                      {material.label}
                    </button>
                    <div className="hidden group-hover:block absolute z-20 bottom-full mb-2 left-1/2 -translate-x-1/2 w-60 bg-white border border-primary/20 rounded-lg shadow-lg p-3 text-xs text-gray-600">
                      <p className="text-gray-500 mb-1">{material.composition}</p>
                      <p>{material.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Size */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-800">Size</label>
                <button onClick={handleSizeGuideClick} className="text-xs text-primary hover:underline">Size Guide</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {SIZES.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`py-2 px-4 border-2 rounded-md text-sm font-medium transition ${
                      selectedSize === size
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-primary/20 text-gray-600 hover:border-primary/50'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              {!selectedSize && (
                <p className="text-xs text-gray-500 mt-2">Please select a size to add to cart</p>
              )}
            </div>

            {/* Quantity */}
            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-800 mb-2">Quantity</label>
              <div className="flex items-center gap-4 w-fit">
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="w-10 h-10 border border-primary/30 rounded-md hover:border-primary transition flex items-center justify-center text-gray-800 text-lg"
                >
                  −
                </button>
                <span className="w-8 text-center font-medium text-gray-800">{quantity}</span>
                <button
                  onClick={() => setQuantity(q => q + 1)}
                  className="w-10 h-10 border border-primary/30 rounded-md hover:border-primary transition flex items-center justify-center text-gray-800 text-lg"
                >
                  +
                </button>
              </div>
            </div>

            {/* CTA Buttons */}
            <button
              onClick={handleAddToCart}
              className={`w-full font-medium py-3 px-6 rounded-md transition flex items-center justify-center gap-2 mb-3 ${
                addedToCart
                  ? 'bg-green-600 text-white'
                  : 'bg-primary hover:bg-primary-dark text-white'
              }`}
            >
              <ShoppingBag size={20} />
              {addedToCart ? 'Added to Cart!' : 'Add to Cart'}
            </button>

            {/* Shipping info */}
            <div className="mt-8 pt-8 border-t border-primary/20 space-y-4">
              <div className="flex gap-4">
                <Truck className="text-primary flex-shrink-0" size={20} />
                <div>
                  <p className="font-medium text-sm text-gray-800">Free Shipping</p>
                  <p className="text-xs text-gray-500">On orders over $75</p>
                </div>
              </div>
              <div className="flex gap-4">
                <RotateCcw className="text-primary flex-shrink-0" size={20} />
                <div>
                  <p className="font-medium text-sm text-gray-800">Easy Returns</p>
                  <p className="text-xs text-gray-500">30-day return policy</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Shield className="text-primary flex-shrink-0" size={20} />
                <div>
                  <p className="font-medium text-sm text-gray-800">Secure Checkout</p>
                  <p className="text-xs text-gray-500">SSL encrypted transactions</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductViewPage;